<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { provide, toRef } from 'vue'
import { cn } from '@ds/lib/utils'
import { INPUT_GROUP_SIZE, INPUT_GROUP_SIZE_CLASS, type InputSize } from '.'

const props = withDefaults(defineProps<{
  /** Same scale as `DSInput` — read by every child (`DSInputGroupInput`/`Addon`/`Button`/`Text`). */
  size?: InputSize
  class?: HTMLAttributes['class']
}>(), {
  size: 'md',
})

provide(INPUT_GROUP_SIZE, toRef(props, 'size'))
</script>

<template>
  <div
    data-slot="input-group"
    role="group"
    :class="cn(
      'group/input-group relative flex w-full items-center outline-none font-ds',
      // Matches DSInput at the same size: card fill, ds radius, same height scale.
      'bg-card border border-input rounded-ds-md',
      'transition-[border-color,box-shadow] duration-ds-fast ease-ds-standard',
      INPUT_GROUP_SIZE_CLASS[size],
      'min-w-0 has-[>textarea]:h-auto',

      // Variants based on alignment.
      'has-[>[data-align=inline-start]]:[&>input]:pl-2',
      'has-[>[data-align=inline-end]]:[&>input]:pr-2',
      'has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3',
      'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3',

      // Focus state.
      // The group is the control, so the ring is the group's. shadcn uses a
      // ring + offset; @ds uses the Iris box-shadow and a border shift.
      'has-[[data-slot=input-group-control]:focus-visible]:border-ring',
      'has-[[data-slot=input-group-control]:focus-visible]:shadow-ds-focus',

      props.class,
    )"
  >
    <slot />
  </div>
</template>
